import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Pets,
  Schedule,
  LocationOn,
  Star,
  Shield,
  Phone,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { isHandlerSite, HANDLER_SITE_URL } from '../config/siteMode';
import { useAuth } from '../context/AuthContext';
import LocalBusinessJsonLd from '../components/LocalBusinessJsonLd';
import {
  INK, COBALT, CORAL, SHADOW_COBALT, SHADOW_WARM,
  FONT_DISPLAY, FONT_MONO, offsetShadow, pressable, stripeSx,
} from '../theme/kiff';

const LandingPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // The handler site has no marketing landing — go straight to sign-up/apply.
  useEffect(() => {
    if (isHandlerSite) navigate('/auth', { replace: true });
  }, [navigate]);

  // Where the calls-to-action point. AuthPage also bounces signed-in users to
  // their dashboard, so /auth would still end up in the right place — but
  // sending someone who is already signed in through the login page first
  // reads as "you are logged out", which is exactly the confusion reported.
  const ctaTarget = !isAuthenticated
    ? '/auth'
    : user?.is_staff
      ? '/admin/dashboard'
      : '/dashboard';
  const ctaLabel = isAuthenticated ? 'Go to my dashboard' : 'Get Started';

  const features = [
    {
      icon: <Shield sx={{ fontSize: 36, color: COBALT }} />,
      title: 'Vetted + insured',
      description: 'Every handler is background-checked and insured before they can take a single walk.',
      tint: SHADOW_COBALT,
    },
    {
      icon: <LocationOn sx={{ fontSize: 36, color: COBALT }} />,
      title: 'Watch the walk live',
      description: "Follow your dog on the map, get photos as they happen, chat with your handler.",
      tint: SHADOW_WARM,
    },
    {
      icon: <Schedule sx={{ fontSize: 36, color: COBALT }} />,
      title: 'Booked in two taps',
      description: 'On-demand or scheduled. Most walks match with a handler within four hours.',
      tint: SHADOW_WARM,
    },
    {
      icon: <Star sx={{ fontSize: 36, color: COBALT }} />,
      title: 'Rated by owners',
      description: 'Real reviews from real walks. Rate your handler after every service.',
      tint: SHADOW_COBALT,
    },
    {
      icon: <Phone sx={{ fontSize: 36, color: COBALT }} />,
      title: 'Straight talk',
      description: 'Message your handler directly. No call centres, no runaround.',
      tint: SHADOW_COBALT,
    },
    {
      icon: <Pets sx={{ fontSize: 36, color: COBALT }} />,
      title: 'Your pets, on file',
      description: 'Temperament, meds, the vet’s number — handlers see what matters before they arrive.',
      tint: SHADOW_WARM,
    },
  ];

  return (
    <Box>
      {/* Structured data for Google's business result — owner site only. */}
      <LocalBusinessJsonLd />
      {/* ── Hero — warm paper ground, display type, sticker energy ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Grid container spacing={5} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              component="h1"
              sx={{
                fontFamily: FONT_DISPLAY,
                fontWeight: 900,
                fontStretch: '110%',
                textTransform: 'uppercase',
                letterSpacing: '-0.025em',
                lineHeight: 0.98,
                fontSize: { xs: 44, md: 64 },
                mb: 2,
              }}
            >
              Lekker walks,{' '}
              <Box component="span" sx={{ color: COBALT }}>
                sorted.
              </Box>
            </Typography>
            <Typography
              sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary', mb: 4, maxWidth: 440 }}
            >
              Vetted local handlers for walks, sitting, grooming and training.
              Watch the walk live, pay from your wallet, get your person back
              happy. From R100.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate(ctaTarget)}
              >
                {ctaLabel}
              </Button>
              <Button
                size="large"
                href={HANDLER_SITE_URL}
                sx={{
                  backgroundColor: '#FFFFFF',
                  color: INK,
                  border: `2.5px solid ${INK}`,
                  ...pressable(CORAL),
                  '&:hover': { backgroundColor: '#FFF2E6', boxShadow: offsetShadow(CORAL) },
                }}
              >
                Earn as a handler
              </Button>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            {/* Illustration slot. Art direction: flat sticker-style, thick
                keyline, coral/cobalt fills — commissioned work. The striped
                placeholder is part of the design language, not an apology. */}
            <Box sx={{ position: 'relative', mt: { xs: 2, md: 0 } }}>
              <Box
                sx={{
                  height: { xs: 190, md: 320 },
                  border: `2.5px solid ${INK}`,
                  borderRadius: '14px',
                  ...stripeSx(),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: offsetShadow(SHADOW_COBALT, 5),
                }}
              >
                <Pets sx={{ fontSize: 72, color: INK, opacity: 0.35 }} />
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: -14,
                  right: 10,
                  transform: 'rotate(6deg)',
                  backgroundColor: COBALT,
                  color: '#FFF',
                  border: `2.5px solid ${INK}`,
                  borderRadius: 99,
                  px: 2,
                  py: 0.75,
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 900,
                  fontSize: 15,
                  textTransform: 'uppercase',
                }}
              >
                From R100
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* ── Why PetTrek ── */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h1" component="h2" sx={{ mb: 1 }}>
            Why PetTrek?
          </Typography>
          <Typography sx={{ color: 'text.secondary', maxWidth: 560 }}>
            Trusted handlers in your neighbourhood who treat your pet like
            family — with the receipts to prove it.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 1,
                  boxShadow: offsetShadow(feature.tint, 5),
                }}
              >
                <CardContent>
                  <Box sx={{ mb: 1.5 }}>{feature.icon}</Box>
                  <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Closing CTA ── */}
      <Box sx={{ borderTop: `2.5px solid ${INK}`, py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h1" component="h2" sx={{ mb: 1.5 }}>
              Your dog is ready. Are you?
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 4 }}>
              Walks from{' '}
              <Box component="span" sx={{ fontFamily: FONT_MONO, fontWeight: 600, color: INK }}>
                R100
              </Box>
              {' '}— booked in about a minute.
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              onClick={() => navigate(ctaTarget)}
              sx={{ px: 6 }}
            >
              Book a walk
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
